import { Card, CardHeader, CardBody, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import type { CommandCenter } from '@/services/marketingIntegrations.service';
import { formatMoney, formatNumber } from './derive';

interface Props {
  cc: CommandCenter;
  targetCpa: number;
}

type Status = 'good' | 'warn' | 'bad' | 'pending';

interface Kpi {
  metric: string;
  target: string;
  current: string;
  status: Status;
  owner: string;
  cadence: string;
}

const STATUS_BADGE: Record<Status, { variant: 'success' | 'warning' | 'error' | 'neutral'; icon: string; label: string }> = {
  good:    { variant: 'success', icon: '✓', label: 'On target' },
  warn:    { variant: 'warning', icon: '⚠', label: 'Off target' },
  bad:     { variant: 'error',   icon: '✗', label: 'Off target' },
  pending: { variant: 'neutral', icon: '–', label: 'Pending' },
};

function statusByThreshold(current: number, target: number, lower: number = target * 0.5, higher: number = target * 0.85): Status {
  if (current === 0 || !isFinite(current)) return 'pending';
  if (current >= higher) return 'good';
  if (current >= lower) return 'warn';
  return 'bad';
}

function statusByCpa(current: number, target: number): Status {
  if (current === 0 || !isFinite(current)) return 'pending';
  if (current <= target) return 'good';
  if (current <= target * 1.5) return 'warn';
  return 'bad';
}

export default function KpiTab({ cc, targetCpa }: Props) {
  const ga = cc.dashboard.google_analytics;
  const gads = cc.dashboard.google_ads;
  const meta = cc.dashboard.meta_ads;
  const organic = cc.organic;
  const gsc = cc.gsc;

  const totalSpend = (gads?.configured ? gads.spend : 0) + (meta?.configured ? meta.spend : 0);
  const totalConv =
    (ga?.configured ? ga.conversions : 0) +
    (gads?.configured ? gads.conversions : 0) +
    (meta?.configured ? meta.conversions : 0);
  const blendedCpa = totalConv > 0 ? totalSpend / totalConv : 0;

  // 1. Primary business KPIs
  const business: Kpi[] = [
    { metric: 'Monthly conversions', target: '100', current: formatNumber(Math.round(totalConv)),
      status: statusByThreshold(totalConv, 100), owner: 'CMO', cadence: 'Weekly' },
    { metric: 'Blended CPA', target: formatMoney(targetCpa), current: blendedCpa > 0 ? formatMoney(blendedCpa) : '—',
      status: blendedCpa > 0 ? statusByCpa(blendedCpa, targetCpa) : 'pending', owner: 'CMO', cadence: 'Weekly' },
    { metric: 'Total spend (mo)', target: formatMoney(25000), current: totalSpend > 0 ? formatMoney(totalSpend) : '—',
      status: totalSpend > 0 && totalSpend <= 30000 ? 'good' : totalSpend > 30000 ? 'warn' : 'pending',
      owner: 'CMO', cadence: 'Daily' },
    { metric: 'Spend / conversion ratio', target: '<25% of CAC', current: '—', status: 'pending', owner: 'Finance', cadence: 'Monthly' },
  ];

  // 2. Site / content KPIs (GA4)
  const site: Kpi[] = [
    { metric: 'Organic sessions / mo', target: '5,000', current: organic?.ok ? formatNumber(organic.sessions ?? 0) : '—',
      status: organic?.ok ? statusByThreshold(organic.sessions ?? 0, 5000) : 'pending', owner: 'SEO', cadence: 'Weekly' },
    { metric: 'Organic conversions / mo', target: '50', current: organic?.ok ? formatNumber(organic.conversions ?? 0) : '—',
      status: organic?.ok ? statusByThreshold(organic.conversions ?? 0, 50) : 'pending', owner: 'SEO', cadence: 'Weekly' },
    { metric: 'Bounce rate', target: '<55%', current: organic?.ok ? `${(organic.bounceRate ?? 0).toFixed(1)}%` : '—',
      status: organic?.ok ? ((organic.bounceRate ?? 100) <= 55 ? 'good' : (organic.bounceRate ?? 100) <= 70 ? 'warn' : 'bad') : 'pending',
      owner: 'CMO', cadence: 'Weekly' },
    { metric: 'Pages w/ 500+ sessions, 0 conv', target: '0', current: cc.contentPages?.ok ? `${(cc.contentPages.rows ?? []).filter((p) => p.sessions >= 500 && p.conversions === 0).length}` : '—',
      status: cc.contentPages?.ok ? ((cc.contentPages.rows ?? []).filter((p) => p.sessions >= 500 && p.conversions === 0).length === 0 ? 'good' : 'bad') : 'pending',
      owner: 'CMO', cadence: 'Weekly' },
  ];

  // 3. SEO KPIs (Search Console)
  const seoKpis: Kpi[] = [
    { metric: 'GSC clicks (30d)', target: '2,500', current: gsc?.ok ? formatNumber(gsc.totalClicks ?? 0) : '—',
      status: gsc?.ok ? statusByThreshold(gsc.totalClicks ?? 0, 2500) : 'pending', owner: 'SEO', cadence: 'Weekly' },
    { metric: 'Avg position', target: '<15', current: gsc?.ok ? (gsc.avgPosition ?? 0).toFixed(1) : '—',
      status: gsc?.ok ? ((gsc.avgPosition ?? 99) <= 15 ? 'good' : (gsc.avgPosition ?? 99) <= 25 ? 'warn' : 'bad') : 'pending',
      owner: 'SEO', cadence: 'Weekly' },
    { metric: 'Avg CTR', target: '>3%', current: gsc?.ok ? `${(gsc.avgCtr ?? 0).toFixed(2)}%` : '—',
      status: gsc?.ok ? ((gsc.avgCtr ?? 0) >= 3 ? 'good' : (gsc.avgCtr ?? 0) >= 1.5 ? 'warn' : 'bad') : 'pending',
      owner: 'SEO', cadence: 'Weekly' },
    { metric: 'Page-1 keywords', target: '50', current: gsc?.ok ? `${(gsc.topQueries ?? []).filter((q) => q.position <= 10).length}` : '—',
      status: gsc?.ok ? statusByThreshold((gsc.topQueries ?? []).filter((q) => q.position <= 10).length, 50) : 'pending',
      owner: 'SEO', cadence: 'Monthly' },
  ];

  // 4. Paid channel KPIs
  const paid: Kpi[] = [
    { metric: 'Google Ads CPA', target: formatMoney(targetCpa), current: gads?.configured && gads.conversions > 0 ? formatMoney(gads.spend / gads.conversions) : '—',
      status: gads?.configured && gads.conversions > 0 ? statusByCpa(gads.spend / gads.conversions, targetCpa) : 'pending',
      owner: 'Paid Media', cadence: 'Daily' },
    { metric: 'Meta Ads CPA', target: formatMoney(targetCpa), current: meta?.configured && meta.conversions > 0 ? formatMoney(meta.spend / meta.conversions) : '—',
      status: meta?.configured && meta.conversions > 0 ? statusByCpa(meta.spend / meta.conversions, targetCpa) : 'pending',
      owner: 'Paid Media', cadence: 'Daily' },
    { metric: 'Active campaigns', target: '<25', current: `${(cc.campaigns?.rows ?? []).length}`,
      status: ((cc.campaigns?.rows ?? []).length === 0 ? 'pending' : (cc.campaigns?.rows ?? []).length <= 25 ? 'good' : 'warn'),
      owner: 'Paid Media', cadence: 'Weekly' },
    { metric: 'Campaigns w/ ROAS ≥3×', target: '≥5', current: `${(cc.campaigns?.rows ?? []).filter((c) => (c.roas ?? 0) >= 3).length}`,
      status: statusByThreshold((cc.campaigns?.rows ?? []).filter((c) => (c.roas ?? 0) >= 3).length, 5),
      owner: 'Paid Media', cadence: 'Weekly' },
  ];

  // 5. Infrastructure / tracking
  const infra: Kpi[] = [
    { metric: 'GA4 connected', target: 'Connected', current: ga?.configured ? '✓ active' : 'not connected',
      status: ga?.configured ? 'good' : 'bad', owner: 'Engineering', cadence: '—' },
    { metric: 'Google Ads connected', target: 'Connected', current: gads?.configured ? '✓ active' : 'not connected',
      status: gads?.configured ? 'good' : 'bad', owner: 'Engineering', cadence: '—' },
    { metric: 'Meta Ads connected', target: 'Connected', current: meta?.configured ? '✓ active' : 'not connected',
      status: meta?.configured ? 'good' : 'bad', owner: 'Engineering', cadence: '—' },
    { metric: 'Search Console connected', target: 'Connected', current: gsc?.ok ? '✓ active' : 'not connected',
      status: gsc?.ok ? 'good' : 'bad', owner: 'Engineering', cadence: '—' },
    { metric: 'Google enhanced conversions', target: 'Enabled', current: '—', status: 'pending', owner: 'Engineering', cadence: 'One-time' },
    { metric: 'Meta CAPI server-side', target: 'Enabled', current: '—', status: 'pending', owner: 'Engineering', cadence: 'One-time' },
  ];

  const sections: { title: string; kpis: Kpi[] }[] = [
    { title: 'Primary business KPIs', kpis: business },
    { title: 'Site & content (GA4)', kpis: site },
    { title: 'SEO (Search Console)', kpis: seoKpis },
    { title: 'Paid channels', kpis: paid },
    { title: 'Infrastructure & tracking', kpis: infra },
  ];

  return (
    <div className="p-6 space-y-5">
      {sections.map((section) => (
        <Card key={section.title}>
          <CardHeader><CardTitle>{section.title}</CardTitle></CardHeader>
          <CardBody className="-mx-4 -mb-4 sm:-mx-5 sm:-mb-5">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                    <th className="px-4 py-2.5 text-left font-semibold">Metric</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Target</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Current</th>
                    <th className="px-3 py-2.5 text-center font-semibold">Status</th>
                    <th className="px-3 py-2.5 text-left font-semibold">Owner</th>
                    <th className="px-3 py-2.5 text-left font-semibold">Cadence</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {section.kpis.map((k) => {
                    const s = STATUS_BADGE[k.status];
                    return (
                      <tr key={k.metric} className="hover:bg-slate-50">
                        <td className="px-4 py-2.5 font-medium text-slate-900">{k.metric}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{k.target}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-slate-900">{k.current}</td>
                        <td className="px-3 py-2.5 text-center">
                          <Badge variant={s.variant} size="sm">{s.icon} {s.label}</Badge>
                        </td>
                        <td className="px-3 py-2.5"><Badge variant="info" size="sm">{k.owner}</Badge></td>
                        <td className="px-3 py-2.5 text-slate-600 text-xs">{k.cadence}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      ))}
    </div>
  );
}
